<template>
  <v-dialog
    :value="value"
    max-width="560"
    @input="(open) => $emit('input', open)"
  >
    <v-card class="gid-card">
      <v-card-title class="gid-title">{{
        $t("groupInvite.title")
      }}</v-card-title>
      <v-card-text>
        <i18n path="groupInvite.sub" tag="p" class="gid-sub">
          <template #code>
            <b v-if="classCode">({{ classCode }})</b>
          </template>
        </i18n>
        <p class="gid-vacancy">
          {{ $tc("groupInvite.vacancies", vacancies, { count: vacancies }) }}
        </p>

        <v-form ref="form" @submit.prevent="submit">
          <v-text-field
            v-model="email"
            :rules="[rules.required, rules.email]"
            solo
            flat
            dense
            background-color="var(--dk-wash, #F1F3F5)"
            :placeholder="$t('groupInvite.emailPlaceholder')"
            type="email"
            :label="$t('groupInvite.emailLabel')"
          />
          <v-btn
            block
            depressed
            color="#A826E1"
            class="white--text gid-btn"
            :loading="saving"
            :disabled="vacancies <= 0"
            type="submit"
          >
            <v-icon small left>mdi-link-variant</v-icon
            >{{ $t("groupInvite.generate") }}
          </v-btn>
        </v-form>

        <v-alert
          v-if="created"
          type="success"
          text
          dense
          class="mt-4"
          icon="mdi-check-circle-outline"
        >
          <i18n path="groupInvite.sentTo" tag="span">
            <template #email>
              <b>{{ created.email }}</b>
            </template>
          </i18n>
          <div class="gid-link mt-2">
            <span class="gid-link__text">{{ created.link }}</span>
            <v-btn small depressed @click="copy(created.link)">
              <v-icon small left>mdi-content-copy</v-icon
              >{{ $t("common.copy") }}
            </v-btn>
          </div>
        </v-alert>

        <student-invite-list
          class="mt-4"
          :title="$t('groupInvite.listTitle')"
          :invites="invites"
          :loading="loadingList"
          @cancel="cancel"
          @copy="copy"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">{{
          $t("common.close")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import StudentInviteList from "@/components/connect/partner/StudentInviteList.vue";

// Card 86akng14v: botao "Vincular novo aluno" da gestao de turma.
export default {
  name: "GroupInviteDialog",
  components: { StudentInviteList },
  props: {
    value: { type: Boolean, default: false },
    groupId: { type: String, required: true },
    classCode: { type: String, default: "" },
    capacity: { type: Number, default: 0 },
    membersCount: { type: Number, default: 0 },
  },
  data() {
    return {
      email: "",
      saving: false,
      loadingList: false,
      created: null,
      invites: [],
      rules: {
        required: (v) => !!v || this.$t("common.requiredFieldDot"),
        email: (v) =>
          /.+@.+\..+/.test(v || "") || this.$t("common.invalidEmail"),
      },
    };
  },
  computed: {
    // Convites em aberto reservam vaga: a API recusa passar da capacidade.
    vacancies() {
      const pending = this.invites.filter(
        (invite) => invite.status !== "used"
      ).length;
      return Math.max(this.capacity - this.membersCount - pending, 0);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.created = null;
        this.loadInvites();
      }
    },
  },
  methods: {
    async loadInvites() {
      this.loadingList = true;
      try {
        const res = await partnerApi.listStudentInvites({
          classId: this.groupId,
        });
        this.invites = res.data?.data || [];
      } catch (error) {
        this.invites = [];
      } finally {
        this.loadingList = false;
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.created = null;
      try {
        const res = await partnerApi.createGroupStudentInvite(
          this.groupId,
          this.email.trim()
        );
        this.created = res.data;
        this.email = "";
        this.$refs.form.resetValidation();
        this.alert(this.$t("groupInvite.generated"), 0);
        this.loadInvites();
      } catch (error) {
        const raw = error?.response?.data?.message;
        const message = Array.isArray(raw) ? raw[0] : raw;
        this.alert(message || this.$t("groupInvite.generateFailed"), 1);
      } finally {
        this.saving = false;
      }
    },
    async cancel(invite) {
      try {
        await partnerApi.cancelStudentInvite(invite.id);
        if (this.created && this.created.id === invite.id) this.created = null;
        this.alert(this.$t("groupInvite.canceled"), 0);
        this.loadInvites();
      } catch (error) {
        this.alert(this.$t("groupInvite.cancelFailed"), 1);
      }
    },
    async copy(link) {
      try {
        await navigator.clipboard.writeText(link);
        this.alert(this.$t("common.linkCopied"), 0);
      } catch (error) {
        this.alert(this.$t("common.copyFailed"), 2);
      }
    },
    alert(text, type) {
      this.$root.$emit("display-alert", { text, type });
    },
  },
};
</script>

<style scoped>
.gid-title {
  font-weight: 600;
  color: var(--dk-ink, #1f1147);
}
.gid-sub {
  color: var(--dk-muted, #6b6480);
  font-size: 0.9rem;
}
.gid-vacancy {
  font-weight: 500;
  color: var(--dk-ink-soft, #3d3653);
}
.gid-btn {
  border-radius: 8px;
  text-transform: none;
}
.gid-link {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.gid-link__text {
  word-break: break-all;
  font-family: monospace;
  font-size: 0.85rem;
}
</style>
